import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import uePrehledApi, { IPrehled, IPrehledPozadavek } from 'services/specialniSluzby/uePrehled';

import routePathEnum from 'enums/routePathEnum';

import { typPozadavkuSelect } from 'data/codebooks/specialniSluzby/typPozadavku';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';

const HistorickePozadavky: React.FC = () => {
    const { aktivniFirma } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<IPrehled>();
    // const [isinList, setIsinList] = useState<{ value: string; label: string }[]>([]);
    const { setLevel } = useProfily();
    const [pagination, setPagination] = useState<{
        offset: number;
        page: number;
        limit: number;
        order: string;
    }>({
        offset: 0,
        page: 1,
        limit: 10,
        order: 'datum:desc'
    });

    const columns = [
        {
            title: <TranslateText>TYP</TranslateText>,
            dataIndex: 'typ',
            key: 'typ',
            render: (text: string) => {
                return <TranslateText>{'SS_PREHLED_POZADAVEK_' + text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>ISIN</TranslateText>,
            dataIndex: 'isin',
            key: 'isin'
        },
        {
            title: <TranslateText>DATUM</TranslateText>,
            dataIndex: 'datum',
            key: 'datum',
            render: (text: string) => {
                return dayjs(text).format('DD.MM.YYYY');
            }
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>SS_PREHLED_POCET_UCTU</TranslateText>,
            dataIndex: 'pocetuctu',
            key: 'pocetuctu',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>POCET_UCTU_OVERENE_2</TranslateText>,
            dataIndex: 'pocetuctuovereno',
            key: 'pocetuctuovereno',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>POCET_USPESNYCH</TranslateText>,
            dataIndex: 'pocetuspesnychpozadavku',
            key: 'pocetuspesnychpozadavku',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>POCET_NEUSPESNYCH</TranslateText>,
            dataIndex: 'pocetneuspesnychpozadavku',
            key: 'pocetneuspesnychpozadavku',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: '',
            dataIndex: 'detail',
            key: 'detail',
            render: (_text: string, row: IPrehledPozadavek, index: number) => (
                <AnchorComponent
                    id={`detailBtn${index}`}
                    onClick={() => {
                        setLevel('specialniSluzby', {
                            detail: {
                                ...row
                            }
                        });
                        navigateWorkaround(
                            `/firma/${aktivniFirma?.iducet}${
                                row.dlt
                                    ? routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT
                                    : routePathEnum.SLUZBY_DETAIL_POZADAVKU
                            }`
                        );
                    }}
                >
                    <TranslateText>ZOBRAZIT_DETAIL</TranslateText>
                </AnchorComponent>
            )
        }
    ];

    const loadData = (offset?: number) => {
        setLoading(true);
        uePrehledApi({
            isin: form.getFieldValue('isin') ? form.getFieldValue('isin') : undefined,
            datum: form.getFieldValue('datum') ? dayjs(form.getFieldValue('datum')).format('YYYY-MM-DD') : undefined,
            typ: form.getFieldValue('typ') ? form.getFieldValue('typ') : undefined,
            offset: offset !== undefined ? offset : pagination.offset,
            limit: pagination.limit,
            order: pagination.order
        })
            .then((resp: IPrehled) => {
                // if (!filter) {
                //     const tmpArr: string[] = [];
                //     const tmpSelectArr: { value: string; label: string }[] = [];
                //     resp.pozadavky.forEach(item => {
                //         if (!tmpArr.includes(item.isin)) {
                //             tmpArr.push(item.isin);
                //             tmpSelectArr.push({
                //                 value: item.isin,
                //                 label: item.isin
                //             });
                //         }
                //     });
                //     setIsinList(tmpSelectArr);
                // }

                setData(resp);
            })
            .catch((err: any) => {
                err.response?.status === 404 && setData(undefined);
                setErrStatus(err.response?.status);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper loading={loading}>
            <Title level={4} text="PREHLED_POZADAVKU" />
            {errStatus ? (
                <Row>
                    <Col span={16}>
                        {errStatus === 404 ? (
                            <Paragraph text="RESPONSE_ERROR_404" />
                        ) : errStatus ? (
                            <Paragraph text="SLUZBA_NEDOSTUPNA" />
                        ) : null}
                        <Row className="button-row">
                            <ButtonComponent
                                id="zpetBtn"
                                buttontext="ZPET"
                                type="default"
                                onClick={() => {
                                    setErrStatus(0);
                                }}
                            />
                        </Row>
                    </Col>
                </Row>
            ) : (
                <>
                    <FormWrapper onFinish={() => {}} outForm={form}>
                        <Row gutter={12}>
                            <Col span={8}>
                                <SelectComponent
                                    isFormItem={true}
                                    data={typPozadavkuSelect}
                                    name="typ"
                                    labelParagraph={'TYP'}
                                    notRequired={true}
                                />
                            </Col>
                            <Col span={8}>
                                {/* <SelectComponent
                                    isFormItem={true}
                                    data={isinList}
                                    name="isin"
                                    labelParagraph={'ISIN'}
                                    notRequired={true}
                                /> */}
                                <InputComponent
                                    name="isin"
                                    label="ISIN"
                                    maxLength={12}
                                    notRequired={true}
                                    capitalize={true}
                                />
                            </Col>
                            <Col span={8}>
                                <DatePickerComponent name="datum" label="DATUM" notRequired={true} />
                            </Col>
                        </Row>
                        <Row className="button-row-modal">
                            <ButtonComponent
                                id="filtrSubmitBtn"
                                buttontext="FILTROVAT"
                                onClick={() => {
                                    loadData();
                                }}
                            />
                            <ButtonComponent
                                id="filtrClearBtn"
                                buttontext="VSECHNY"
                                onClick={() => {
                                    form.resetFields();
                                    loadData();
                                }}
                            />
                        </Row>
                    </FormWrapper>
                    <TableComponent
                        rowKey={(_, index) => index || 0}
                        columns={columns}
                        dataSource={data?.pozadavky}
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        pagination={{
                            total: data?.count || 0,
                            onChange: page => {
                                setPagination({
                                    page: page,
                                    offset: (page - 1) * pagination.limit,
                                    limit: pagination.limit,
                                    order: pagination.order
                                });
                                loadData((page - 1) * pagination.limit);
                            },
                            current: pagination.page,
                            showSizeChanger: false
                        }}
                    />
                </>
            )}
        </LoadingWrapper>
    );
};

export default HistorickePozadavky;
